import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { toast } from 'react-toastify';
import { Calendar, Clock, Users, BookOpen, Grid, List, Video } from 'lucide-react';
import {
  formatSessionDate,
  formatTimeUntil,
  getTimeUntilSession,
  getSessionBadge,
  getNextGlobalSession
} from '../utils/sessionHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Función para transformar sesiones de la API a formato curriculum (igual que Dashboard)
const transformSessionsToCurriculum = (sessions) => {
  const pilarGroups = {};
  
  sessions.forEach(session => {
    const pilarName = session.pilar || 'Sin clasificar';
    if (!pilarGroups[pilarName]) {
      pilarGroups[pilarName] = {
        id: pilarName.toLowerCase().replace(/\s+/g, '-'),
        title: pilarName,
        description: `Sesiones del pilar ${pilarName}`,
        totalSessions: 0,
        completedSessions: 0,
        modules: [],
        mentorIds: []
      };
    }
    
    pilarGroups[pilarName].modules.push({
      id: session.id,
      title: session.title,
      durationMin: session.duration || 90,
      status: session.status || 'upcoming',
      startAt: session.date,
      accessUrl: session.meeting_url,
      description: session.description,
      mentorName: session.mentor_name
    });
    
    pilarGroups[pilarName].totalSessions++;
    
    if (session.mentor_id && !pilarGroups[pilarName].mentorIds.includes(session.mentor_id)) {
      pilarGroups[pilarName].mentorIds.push(session.mentor_id);
    }
  });
  
  return Object.values(pilarGroups);
};

export default function SessionsManagement() {
  const [sessions, setSessions] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [viewMode, setViewMode] = useState('curriculum'); // 'table' or 'curriculum'
  const [formData, setFormData] = useState({ 
    title: '', 
    pilar: '', 
    date: '', 
    duration: 90, 
    meeting_url: '', 
    description: '',
    summary: '',
    status: 'upcoming',
    mentor_id: ''
  });
  const { getToken } = useAdminAuth();

  useEffect(() => {
    fetchSessions();
    fetchMentors();
  }, []);

  const fetchSessions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/sessions`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      console.log('📊 Total sesiones recibidas:', response.data.sessions.length);
      setSessions(response.data.sessions);
      
      // Debug: Contar sesiones por pilar
      const pilarCounts = {};
      response.data.sessions.forEach(s => {
        pilarCounts[s.pilar] = (pilarCounts[s.pilar] || 0) + 1;
      });
      console.log('📚 Sesiones por pilar:', pilarCounts);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Error al cargar sesiones');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMentors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/mentors`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setMentors(response.data.mentors || []);
    } catch (error) {
      console.error('Error fetching mentors:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/admin/sessions`, formData, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setShowCreateModal(false);
      fetchSessions();
      setFormData({ title: '', pilar: '', date: '', duration: 90, meeting_url: '', description: '', summary: '', status: 'upcoming', mentor_id: '' });
      toast.success('Sesión creada correctamente');
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Error al crear la sesión');
    }
  };

  const handleEdit = (sessionOrModule) => {
    // Si recibe un module transformado, buscar la sesión original por ID
    const session = sessions.find(s => s.id === sessionOrModule.id) || sessionOrModule;
    
    setEditingSession(session);
    
    // Convertir la fecha ISO a formato datetime-local
    let dateValue = session.date;
    if (dateValue && dateValue.includes('T')) {
      dateValue = dateValue.slice(0, 16);
    }
    
    setFormData({
      title: session.title,
      pilar: session.pilar,
      date: dateValue,
      duration: session.duration || 90,
      meeting_url: session.meeting_url || session.accessUrl || '',
      description: session.description || '',
      summary: session.summary || '',
      status: session.status,
      mentor_id: session.mentor_id || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/sessions/${editingSession.id}`,
        formData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setShowEditModal(false);
      setEditingSession(null);
      fetchSessions();
      setFormData({ title: '', pilar: '', date: '', duration: 90, meeting_url: '', description: '', summary: '', status: 'upcoming', mentor_id: '' });
      toast.success('Sesión actualizada correctamente');
    } catch (error) {
      console.error('Error updating session:', error);
      toast.error('Error al actualizar la sesión');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta sesión?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/sessions/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success('Sesión eliminada');
      fetchSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Error al eliminar la sesión');
    }
  };

  // Transformar sesiones a formato curriculum (igual que Dashboard del estudiante)
  const curriculumData = transformSessionsToCurriculum(sessions);
  
  // Debug log
  console.log('🎯 Pilares generados:', curriculumData.length);
  console.log('📋 Pilares:', curriculumData.map(p => `${p.title} (${p.totalSessions})`));
  
  // Agrupar sesiones por pilar para vista de tabla
  const groupedSessions = sessions.reduce((acc, session) => {
    const pilarName = session.pilar || 'Sin clasificar';
    if (!acc[pilarName]) {
      acc[pilarName] = {
        pilar: pilarName,
        sessions: [],
        totalSessions: 0,
        totalDuration: 0
      };
    }
    acc[pilarName].sessions.push(session);
    acc[pilarName].totalSessions++;
    acc[pilarName].totalDuration += session.duration || 90;
    return acc;
  }, {});

  const pilares = Object.values(groupedSessions);

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      live: 'bg-green-500/10 text-green-400 border-green-500/30',
      completed: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
    };
    return styles[status] || styles.upcoming;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Programa de Estudio (En Vivo)</h1>
          <p className="text-gray-400 mt-1">Gestiona las sesiones en vivo del programa</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Toggle View */}
          <div className="flex items-center gap-2 bg-[#0A0A0A] rounded-lg p-1 border border-[#1a4d5c]/30">
            <button
              onClick={() => setViewMode('curriculum')}
              className={`px-4 py-2 rounded flex items-center gap-2 transition-colors ${
                viewMode === 'curriculum'
                  ? 'bg-[#D4AF37] text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Grid size={18} />
              Curriculum
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded flex items-center gap-2 transition-colors ${
                viewMode === 'table'
                  ? 'bg-[#D4AF37] text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <List size={18} />
              Tabla
            </button>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors"
          >
            + Crear Sesión
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="text-[#D4AF37]" size={24} />
            <h3 className="text-gray-400 text-sm">Total Sesiones</h3>
          </div>
          <p className="text-3xl font-bold text-white">{sessions.length}</p>
        </div>
        
        <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
          <div className="flex items-center gap-3 mb-2">
            <Users className="text-[#D4AF37]" size={24} />
            <h3 className="text-gray-400 text-sm">Pilares</h3>
          </div>
          <p className="text-3xl font-bold text-white">{pilares.length}</p>
        </div>
        
        <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="text-[#D4AF37]" size={24} />
            <h3 className="text-gray-400 text-sm">Horas Totales</h3>
          </div>
          <p className="text-3xl font-bold text-white">
            {Math.round(sessions.reduce((sum, s) => sum + (s.duration || 90), 0) / 60)}h
          </p>
        </div>
        
        <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="text-[#D4AF37]" size={24} />
            <h3 className="text-gray-400 text-sm">Próximas</h3>
          </div>
          <p className="text-3xl font-bold text-white">
            {sessions.filter(s => s.status === 'upcoming').length}
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
        </div>
      )}

      {/* Curriculum View - Replicando Student Dashboard */}
      {!isLoading && viewMode === 'curriculum' && (
        <div className="space-y-6">
          {/* Header dinámico con próxima sesión */}
          {(() => {
            const nextSession = getNextGlobalSession(curriculumData);
            return (
              <div className="bg-gradient-to-br from-teal-500/5 to-transparent border border-teal-500/20 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">Vista de Estudiante</h3>
                    {nextSession ? (
                      <div className="flex items-center space-x-3 text-gray-400">
                        <Calendar size={20} className="text-teal-400" />
                        {nextSession.isLive ? (
                          <p className="text-red-500 font-semibold animate-pulse">
                            🔴 AHORA: {nextSession.pilarTitle} — {nextSession.title}
                          </p>
                        ) : (
                          <p>
                            📅 Próxima sesión: <span className="text-white font-medium">{nextSession.pilarTitle}</span> — {formatSessionDate(nextSession.startAt)} ({formatTimeUntil(nextSession.timeUntil)})
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-400">No hay sesiones programadas próximamente</p>
                    )}
                  </div>
                  <Clock className="text-teal-400" size={32} />
                </div>
              </div>
            );
          })()}
          
          {curriculumData.length === 0 ? (
            <div className="text-center py-20 bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30">
              <BookOpen className="mx-auto text-gray-600 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-400">No hay sesiones programadas</h3>
              <p className="text-sm text-gray-500 mt-2">Crea tu primera sesión para comenzar</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {curriculumData.map((pilar) => {
                const nextPilarSession = pilar.modules.find(m => m.status === 'upcoming' || m.status === 'live');
                const hasLiveSession = pilar.modules.some(m => m.status === 'live');
                
                return (
                  <div
                    key={pilar.id}
                    className={`bg-gradient-to-br from-white/5 to-transparent border rounded-xl p-6 transition-all ${
                      hasLiveSession
                        ? 'border-red-500/50 shadow-lg shadow-red-500/20'
                        : 'border-[#1a4d5c]/30 hover:border-teal-500/30'
                    }`}
                  >
                    {/* Header del Pilar */}
                    <div className="mb-4">
                      <h3 className="text-lg font-bold text-white mb-2">{pilar.title}</h3>
                      <p className="text-sm text-gray-400 mb-3">{pilar.description}</p>
                      
                      {/* Info de próxima sesión */}
                      {nextPilarSession && (
                        <div className="flex items-center space-x-2 text-sm mb-2">
                          <Clock size={16} className="text-teal-400" />
                          {nextPilarSession.status === 'live' ? (
                            <span className="text-red-500 font-semibold">En vivo ahora</span>
                          ) : (
                            <span className="text-gray-400">
                              Próxima: {formatSessionDate(nextPilarSession.startAt)}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500">{pilar.totalSessions} sesiones totales</p>
                    </div>

                    {/* Módulos/Sesiones */}
                    <div className="space-y-3">
                      {pilar.modules.map((module) => {
                        const badge = getSessionBadge(module);
                        
                        return (
                          <div
                            key={module.id}
                            className={`p-4 rounded-lg border transition-all ${
                              module.status === 'live'
                                ? 'bg-red-500/10 border-red-500/50 shadow-md'
                                : 'bg-black/30 border-[#1a4d5c]/20 hover:border-[#D4AF37]/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <p className="text-sm font-medium text-white">{module.title}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded ${badge.color}`}>
                                    {badge.icon} {badge.text}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-3 text-xs text-gray-500 mb-2">
                                  <span className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    {formatSessionDate(module.startAt)}
                                  </span>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {module.durationMin} min
                                  </span>
                                </div>
                                
                                {module.mentorName && (
                                  <p className="text-xs text-[#D4AF37] mb-2">
                                    👤 {module.mentorName}
                                  </p>
                                )}
                                
                                {/* Botones de acción admin */}
                                <div className="flex items-center gap-2 mt-3">
                                  {module.accessUrl && (
                                    <a
                                      href={module.accessUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`text-xs px-3 py-1.5 rounded-md font-medium transition-all ${
                                        module.status === 'live'
                                          ? 'bg-red-500 text-white hover:bg-red-400'
                                          : 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30'
                                      }`}
                                    >
                                      {module.status === 'live' ? '🔴 Entrar ahora' : '📺 Ver detalles'}
                                    </a>
                                  )}
                                  <button
                                    onClick={() => handleEdit(module)}
                                    className="text-xs px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-md hover:bg-blue-500/20 transition-colors"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    onClick={() => handleDelete(module.id)}
                                    className="text-xs px-3 py-1.5 bg-red-500/10 text-red-400 rounded-md hover:bg-red-500/20 transition-colors"
                                  >
                                    Eliminar
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Table View */}
      {!isLoading && viewMode === 'table' && (
        <div className="bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30 overflow-hidden">
          {sessions.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="mx-auto text-gray-600 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-400">No hay sesiones programadas</h3>
              <p className="text-sm text-gray-500 mt-2">Crea tu primera sesión para comenzar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-black/30">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Título</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Pilar</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Fecha</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Duración</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1a4d5c]/30">
                  {sessions.map((session) => (
                    <tr key={session.id} className="hover:bg-black/20 transition-colors">
                      <td className="px-6 py-4 text-white font-medium">{session.title}</td>
                      <td className="px-6 py-4 text-gray-300">{session.pilar}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {new Date(session.date).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4 text-gray-300">{session.duration || 90} min</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                            session.status
                          )}`}
                        >
                          {session.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(session)}
                            className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded hover:bg-blue-500/20 transition-colors text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(session.id)}
                            className="px-3 py-1 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 transition-colors text-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-75" onClick={() => setShowCreateModal(false)}></div>
            <div className="relative bg-[#0A0A0A] rounded-lg max-w-lg w-full p-6 border border-[#1a4d5c]/30">
              <h2 className="text-2xl font-bold text-white mb-6">Nueva Sesión en Vivo</h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Título</label>
                  <input 
                    type="text" 
                    required 
                    value={formData.title} 
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Pilar</label>
                  <input 
                    type="text" 
                    required 
                    value={formData.pilar} 
                    onChange={(e) => setFormData({ ...formData, pilar: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Fecha y Hora</label>
                  <input 
                    type="datetime-local" 
                    required 
                    value={formData.date} 
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Duración (minutos)</label>
                  <input 
                    type="number" 
                    required 
                    value={formData.duration} 
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">URL de la Reunión</label>
                  <input 
                    type="url" 
                    required 
                    value={formData.meeting_url} 
                    onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                    placeholder="https://meet.google.com/..." 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Descripción (opcional)</label>
                  <textarea 
                    value={formData.description} 
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                    rows="3"
                    placeholder="Descripción detallada de la sesión..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Resumen (opcional - se muestra en dashboard estudiante)</label>
                  <textarea 
                    value={formData.summary} 
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                    rows="2"
                    placeholder="Resumen corto para los estudiantes..."
                  />
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button 
                    type="button" 
                    onClick={() => setShowCreateModal(false)} 
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    className="px-4 py-2 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors"
                  >
                    Crear Sesión
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingSession && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-75" onClick={() => setShowEditModal(false)}></div>
            <div className="relative bg-[#0A0A0A] rounded-lg max-w-lg w-full p-6 border border-[#1a4d5c]/30 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-white mb-6">Editar Sesión: {editingSession.title}</h2>
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Título</label>
                  <input 
                    type="text" 
                    required 
                    value={formData.title} 
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Pilar</label>
                  <input 
                    type="text" 
                    required 
                    value={formData.pilar} 
                    onChange={(e) => setFormData({ ...formData, pilar: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Fecha y Hora</label>
                  <input 
                    type="datetime-local" 
                    required 
                    value={formData.date} 
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Duración (minutos)</label>
                  <input 
                    type="number" 
                    required 
                    value={formData.duration} 
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">URL de la Reunión</label>
                  <input 
                    type="url" 
                    required 
                    value={formData.meeting_url} 
                    onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                    placeholder="https://meet.google.com/..." 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Estado</label>
                  <select 
                    value={formData.status} 
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                  >
                    <option value="upcoming">Upcoming (Próxima)</option>
                    <option value="live">Live (En Vivo)</option>
                    <option value="replay">Replay (Grabación)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Mentor Asignado</label>
                  <select 
                    value={formData.mentor_id} 
                    onChange={(e) => setFormData({ ...formData, mentor_id: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
                  >
                    <option value="">Sin mentor asignado</option>
                    {mentors.map((mentor) => (
                      <option key={mentor.id} value={mentor.id}>
                        {mentor.name || mentor.full_name} {mentor.is_active ? '' : '(Inactivo)'}
                      </option>
                    ))}
                  </select>
                  {formData.mentor_id && (
                    <p className="text-xs text-gray-400 mt-1">
                      Mentor actual: {mentors.find(m => m.id === formData.mentor_id)?.name || mentors.find(m => m.id === formData.mentor_id)?.full_name || 'No encontrado'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Descripción (opcional)</label>
                  <textarea 
                    value={formData.description} 
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                    rows="3"
                    placeholder="Descripción detallada de la sesión..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Resumen (opcional - se muestra en dashboard estudiante)
                  </label>
                  <textarea 
                    value={formData.summary} 
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })} 
                    className="w-full px-4 py-2 bg-black/30 border border-[#1a4d5c]/30 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none" 
                    rows="2"
                    placeholder="Resumen corto para los estudiantes..."
                  />
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingSession(null);
                    }} 
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    className="px-4 py-2 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors"
                  >
                    Guardar Cambios
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
